import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { UnlistedStockStatus } from '../../types';
import type { UnlistedStock } from '../../types';

const UnlistedStockEditor: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [formData, setFormData] = useState<Partial<UnlistedStock>>({
        name: '',
        symbol: '',
        slug: '',
        sector: '',
        price: 0,
        min_investment: 0,
        description: '',
        status: UnlistedStockStatus.HIDDEN,
        is_featured: false,
        currency: 'INR',
        image_url: '',
        pricing_slabs: []
    });

    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(isEditMode);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (isEditMode) {
            fetchStock();
        }
    }, [id]);

    const fetchStock = async () => {
        try {
            const response = await api.get(`/unlisted-stocks/${id}`);
            if (response.data.success) {
                setFormData(response.data.data);
            }
        } catch (error) {
            console.error('Failed to fetch stock', error);
            alert('Failed to load stock details');
        } finally {
            setInitialLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;

        let finalValue: any = value;
        if (type === 'number') {
            finalValue = parseFloat(value);
        } else if (type === 'checkbox') {
            finalValue = (e.target as HTMLInputElement).checked;
        }

        setFormData(prev => ({
            ...prev,
            [name]: finalValue
        }));
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const uploadData = new FormData();
        uploadData.append('file', file);

        setUploading(true);
        try {
            const response = await api.post('/upload', uploadData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                setFormData(prev => ({
                    ...prev,
                    image_url: response.data.data.url
                }));
            }
        } catch (error) {
            console.error('Upload failed', error);
            alert('Failed to upload image');
        } finally {
            setUploading(false);
        }
    };

    const handleSlabChange = (index: number, field: 'min_qty' | 'price', value: string) => {
        const newSlabs = [...(formData.pricing_slabs || [])];
        newSlabs[index] = {
            ...newSlabs[index],
            [field]: parseFloat(value)
        };
        setFormData(prev => ({ ...prev, pricing_slabs: newSlabs }));
    };

    const addSlab = () => {
        setFormData(prev => ({
            ...prev,
            pricing_slabs: [...(prev.pricing_slabs || []), { min_qty: 0, price: 0 }]
        }));
    };

    const removeSlab = (index: number) => {
        setFormData(prev => ({
            ...prev,
            pricing_slabs: (prev.pricing_slabs || []).filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (isEditMode) {
                await api.put(`/unlisted-stocks/${id}`, formData);
            } else {
                await api.post('/unlisted-stocks', formData);
            }
            navigate('/admin/unlisted-stocks');
        } catch (error) {
            console.error('Save failed', error);
            alert('Failed to save stock');
        } finally {
            setLoading(false);
        }
    };

    if (initialLoading) return <div className="p-8">Loading...</div>;

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <h1 className="text-2xl font-bold text-slate-900 mb-6">
                {isEditMode ? 'Edit Stock' : 'Add New Stock'}
            </h1>

            <form onSubmit={handleSubmit} className="bg-white shadow-sm border border-slate-200 rounded-xl p-6 space-y-6">

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-slate-700">Company Name</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700">Symbol (Ticker)</label>
                        <input
                            type="text"
                            name="symbol"
                            value={formData.symbol}
                            onChange={handleChange}
                            required
                            className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700">URL Slug (Auto-generated if empty)</label>
                        <input
                            type="text"
                            name="slug"
                            value={formData.slug || ''}
                            onChange={handleChange}
                            placeholder="e.g. swiggy-limited"
                            className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-slate-700">Sector</label>
                        <input
                            type="text"
                            name="sector"
                            value={formData.sector}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700">Status</label>
                        <select
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2"
                        >
                            <option value="ACTIVE">Active</option>
                            <option value="HIDDEN">Hidden</option>
                            <option value="SOLD_OUT">Sold Out</option>
                            <option value="LISTED">Listed</option>
                        </select>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-slate-700">Price (INR)</label>
                        <input
                            type="number"
                            name="price"
                            value={formData.price}
                            onChange={handleChange}
                            step="0.01"
                            required
                            className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700">Min Investment</label>
                        <input
                            type="number"
                            name="min_investment"
                            value={formData.min_investment}
                            onChange={handleChange}
                            step="0.01"
                            className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2"
                        />
                    </div>
                </div>

                {/* Image Upload */}
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Stock Image</label>
                    <div className="flex items-center space-x-4">
                        {formData.image_url && (
                            <img
                                src={formData.image_url.startsWith('http') ? formData.image_url : `/api${formData.image_url}`}
                                alt="Preview"
                                className="h-16 w-16 object-cover rounded-lg bg-slate-100"
                            />
                        )}
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="block w-full text-sm text-slate-500
                                file:mr-4 file:py-2 file:px-4
                                file:rounded-full file:border-0
                                file:text-sm file:font-semibold
                                file:bg-indigo-50 file:text-indigo-700
                                hover:file:bg-indigo-100"
                            disabled={uploading}
                        />
                        {uploading && <span className="text-sm text-slate-500">Uploading...</span>}
                    </div>
                </div>

                {/* Pricing Slabs */}
                <div>
                    <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium text-slate-700">Pricing Slabs</label>
                        <button
                            type="button"
                            onClick={addSlab}
                            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                        >
                            + Add Slab
                        </button>
                    </div>
                    {formData.pricing_slabs?.map((slab, index) => (
                        <div key={index} className="flex space-x-4 mb-2 items-end">
                            <div className="flex-1">
                                <label className="text-xs text-slate-500">Min Qty</label>
                                <input
                                    type="number"
                                    value={slab.min_qty}
                                    onChange={(e) => handleSlabChange(index, 'min_qty', e.target.value)}
                                    className="block w-full rounded-md border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-xs border p-2"
                                />
                            </div>
                            <div className="flex-1">
                                <label className="text-xs text-slate-500">Price</label>
                                <input
                                    type="number"
                                    value={slab.price}
                                    onChange={(e) => handleSlabChange(index, 'price', e.target.value)}
                                    className="block w-full rounded-md border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-xs border p-2"
                                />
                            </div>
                            <button
                                type="button"
                                onClick={() => removeSlab(index)}
                                className="text-red-500 hover:text-red-700 mb-2"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                    ))}
                    {(!formData.pricing_slabs || formData.pricing_slabs.length === 0) && (
                        <p className="text-xs text-slate-400 italic">No specific pricing slabs defined. Standard price will apply.</p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-slate-700">Description (HTML Supported)</label>
                    <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows={6}
                        className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2 font-mono"
                    />
                    <p className="mt-1 text-xs text-slate-500">You can use basic HTML tags for formatting.</p>
                </div>

                <div className="flex items-center">
                    <input
                        id="is_featured"
                        name="is_featured"
                        type="checkbox"
                        checked={formData.is_featured}
                        onChange={handleChange}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_featured" className="ml-2 block text-sm text-slate-900">
                        Feature this stock (display prominently)
                    </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-slate-100">
                    <button
                        type="button"
                        onClick={() => navigate('/admin/unlisted-stocks')}
                        className="px-4 py-2 border border-slate-300 rounded-md shadow-sm text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                        {loading ? 'Saving...' : 'Save Stock'}
                    </button>
                </div>

            </form>
        </div>
    );
};

export default UnlistedStockEditor;
